import React from "react";
import { useFormikContext } from "formik";
import { func } from "prop-types";

import { useFeatureFlags } from "src/shared/components/FeatureFlags";

import { defaultPostSubmitAnswers } from "../../constants/defaultPostSubmitAnswers";
import useFeatureState from "../../utilities/hooks/useFeatureState";
import useUpdatePostSubmitQuestionnaire from "../../utilities/hooks/useUpdatePostSubmitQuestionnaire";
import useUserProfileQuery from "../../utilities/hooks/useUserProfileQuery";
import useReducerActions from "../../utilities/useReducerActions";
import ConfirmationMessage from "../ConfirmationMessage";
import Questionnaire from "../Questionnaire";
import * as S from "./styled";

const POST_SUBMIT_QUESTIONS = ["BuyerAgentQuestion", "FinancingQuestion"];

const PostSubmitQuestionnaire = ({ onSubmit, ...props }) => {
  const {
    postSubmitAnswers,
    questionnaireStepNumber = 0,
    questionnaireTotalSteps
  } = useFeatureState();
  const {
    dispatchSetNextQuestionnaireStep,
    dispatchSetPostSubmitAnswers
  } = useReducerActions();
  const { setFieldError } = useFormikContext();
  const { flags } = useFeatureFlags(
    ["fossa_new_beth_signals_show_financing_question"],
    true
  );
  const { data, error, loading } = useUserProfileQuery();
  const { is_financing_status_stale } = data?.user_profile ?? {};

  const updatePostSubmitQuestionnaire = useUpdatePostSubmitQuestionnaire(
    async () => {
      await onSubmit();
    }
  );

  if (!flags || error || loading) {
    return null;
  }

  const isOnLastStep = questionnaireStepNumber === questionnaireTotalSteps - 1;
  const showFinancingQuestion =
    flags.fossa_new_beth_signals_show_financing_question &&
    is_financing_status_stale;

  const isFinalQuestionAnswered = () =>
    (showFinancingQuestion &&
      postSubmitAnswers.financing_status !== "declined") ||
    (!showFinancingQuestion &&
      postSubmitAnswers.has_buyer_agent !== "declined");

  const handleSubmit = async () => {
    if (isFinalQuestionAnswered()) {
      await updatePostSubmitQuestionnaire();
    } else {
      setFieldError("form_submission", "Please make a selection.");
    }
  };

  const handleSkip = async () => {
    if (isOnLastStep) {
      const currentQuestion = POST_SUBMIT_QUESTIONS[questionnaireStepNumber];
      const postSubmitAnswer = defaultPostSubmitAnswers[currentQuestion];

      dispatchSetPostSubmitAnswers(postSubmitAnswer);
      await updatePostSubmitQuestionnaire(postSubmitAnswer);
    } else {
      dispatchSetNextQuestionnaireStep();
    }
  };

  return (
    <S.Container>
      <S.BackgroundImage />
      <ConfirmationMessage {...props} />
      <S.Separator />
      <S.Text>
        To better support home buyers like you and your process, we’d appreciate
        your input.
      </S.Text>
      <Questionnaire flags={flags} isOnLastStep={isOnLastStep} />
      <S.CaptionText>
        Your answers are confidential. They help to improve StreetEasy only. We
        don’t share them with real estate professionals.
      </S.CaptionText>
      {isOnLastStep && (
        <S.ButtonContainer>
          <S.PrimaryButton onClick={handleSubmit}>Submit</S.PrimaryButton>
        </S.ButtonContainer>
      )}
      <S.SecondaryButton onClick={handleSkip}>Skip</S.SecondaryButton>
    </S.Container>
  );
};

PostSubmitQuestionnaire.propTypes = {
  onSubmit: func.isRequired
};

export default PostSubmitQuestionnaire;
